package ai.jessie.web.work

import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.formSubmitButton
import ai.jessie.web.theme.slideOver
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

/**
 * UC-008 slide-over (`docs/design/screens/08-SlideOver.png`) around the unchanged [workForm], on
 * the theme's [slideOver]. The calendar and the work pages share it; [close] cancels the draft or
 * leaves the editor. Field IDs, labels, problem linking and the submit handler are those of the
 * form.
 */
@Composable
internal fun workEditor(
    work: WorkUiState,
    workActions: WorkActions,
    close: () -> Unit,
    title: String,
    panelId: String = "work-editor",
    formId: String = "work-form",
) {
    val focusReturn = remember { FocusReturn().also { it.pending = true } }
    slideOver(
        panelId = panelId,
        labelledBy = WORK_TITLE_ID,
        close = close,
        busy = work.busy,
        heading = {
            workHeading(title, work, focusReturn)
            workContext(work)?.let {
                P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text(it) }
            }
        },
        actions = {
            themedButton("Abbrechen", close, disabled = work.busy)
            if (work.draft != null && work.error?.authenticationRequired != true)
                formSubmitButton("Speichern", formId, disabled = work.busy)
        },
    ) {
        work.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, work.busy) } }
        if (work.busy)
            P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
                Text("Wird geladen oder gespeichert …")
            }
        workProblems(work.problems)
        if (work.error?.authenticationRequired != true) {
            // Step 9: the invoicing status stays visible while the entry is corrected.
            work.current?.let { invoiceStatus(it.invoiceStatus) }
            workForm(work, workActions, formId)
        }
    }
}

// "Für <Mitglied> · <Wochentag>, <Datum>" from the draft's own choices; nothing is guessed.
private fun workContext(work: WorkUiState): String? {
    val date =
        work.draft?.workDate?.takeIf(::validCalendarDate)
            ?: work.current?.workDate?.takeIf(::validCalendarDate)
    val member =
        work.draft?.memberId?.let { id -> work.selections?.members?.firstOrNull { it.id == id } }
    return listOfNotNull(
            member?.let { "Für ${memberLabel(it.name)}" },
            date?.let { "${calendarWeekday(it)}, ${calendarDateLabel(it)}" },
        )
        .takeIf { it.isNotEmpty() }
        ?.joinToString(" · ")
}

@Composable
internal fun workHeading(title: String, state: WorkUiState, focusReturn: FocusReturn) {
    H2(
        attrs = {
            id(WORK_TITLE_ID)
            attr("tabindex", "-1")
        }
    ) {
        Text(title)
    }
    LaunchedEffect(Unit) {
        // While a problem is shown, the alert or the marked field keeps focus.
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById(WORK_TITLE_ID) as? HTMLElement)?.focus()
        focusReturn.pending = true
    }
}

// A1, A7: every invalid field with its correction; marked fields refer to this list.
@Composable
internal fun workProblems(problems: Map<String, String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id("work-problems") }) {
        problems.forEach { (field, correction) ->
            Li { Text("${workFieldLabel(field)}: $correction") }
        }
    }
}

internal const val WORK_TITLE_ID = "work-title"
